/**
 * NewAssetModal — top-level chooser for "what kind of asset am I creating?"
 *
 * Layered above NewFlowModal rather than replacing it: when the user
 * picks "Integration Flow" we render NewFlowModal (which already owns
 * its own starter-card UI). Picking "Event Design" prompts for a name
 * inline, calls POST /api/event-designs, and navigates to the edit page.
 *
 * Per-page New buttons skip Step 1 and open the asset-specific modal
 * directly (FlowList opens NewFlowModal, EventDesignListPage opens this
 * modal pre-selected on Event Design or its own inline create form).
 *
 * Designed to be extensible: add new AssetKind entries to grow the
 * chooser without touching call sites.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useToast } from './Toast';
import { NewFlowModal } from './NewFlowModal';

export type AssetKind = 'flow' | 'event-design';

interface AssetDef {
  id: AssetKind;
  title: string;
  desc: string;
  /** Single-character icon glyph (kept simple — visual layer only). */
  glyph: string;
  available: boolean;
}

const ASSETS: AssetDef[] = [
  {
    id: 'flow',
    title: 'Integration Flow',
    desc: 'Move data between systems with a visual route. HTTP, JMS, Kafka, file, and more.',
    glyph: '⇄',
    available: true,
  },
  {
    id: 'event-design',
    title: 'Event Design',
    desc: 'Declare Pulsar topics, subscriptions, and replication. Apply the topology to the live broker.',
    glyph: '◇',
    available: true,
  },
];

export interface NewAssetModalProps {
  onClose: () => void;
  /** When set, skip Step 1 and render the asset-specific form directly. */
  initialAsset?: AssetKind;
}

export function NewAssetModal({ onClose, initialAsset }: NewAssetModalProps) {
  const navigate = useNavigate();
  const { push } = useToast();
  const [picked, setPicked] = useState<AssetKind | null>(initialAsset ?? null);

  // Escape closes the chooser. The two child modals own their own Escape
  // handlers (NewFlowModal doesn't, but it's fine — backdrop click works).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // ---- Step 2: Integration Flow → render existing NewFlowModal -------
  if (picked === 'flow') {
    return <NewFlowModal onClose={onClose} />;
  }

  // ---- Step 2: Event Design → inline name form ----------------------
  if (picked === 'event-design') {
    return (
      <NewEventDesignStep
        onClose={onClose}
        onCreated={(id) => {
          onClose();
          navigate(`/event-design/${encodeURIComponent(id)}`);
        }}
        onCreateFailed={(msg) => push('error', msg)}
        onSuccess={(name) => push('success', `Created "${name}"`)}
        onBack={initialAsset ? null : () => setPicked(null)}
      />
    );
  }

  // ---- Step 1: chooser ----------------------------------------------
  return (
    <div
      className="modal-scrim"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="new-asset-modal"
    >
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560 }}
      >
        <header className="modal__header">
          <div>
            <div className="modal__eyebrow">New asset</div>
            <h2 className="modal__title">What are you building?</h2>
          </div>
          <button
            type="button"
            className="btn btn--ghost btn--icon-only"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </header>
        <div className="modal__body" style={{ padding: '12px 24px 20px' }}>
          <div
            className="starter-grid"
            style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}
          >
            {ASSETS.map((a) => (
              <button
                key={a.id}
                type="button"
                className={`starter-card ${a.available ? '' : 'is-disabled'}`}
                onClick={() => a.available && setPicked(a.id)}
                disabled={!a.available}
                data-testid={`asset-${a.id}`}
                style={{ display: 'flex', alignItems: 'center', gap: 12 }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: 'var(--bg-tint)',
                    border: '1px solid var(--border)',
                    display: 'grid',
                    placeItems: 'center',
                    color: 'var(--brand-300)',
                    fontSize: 16,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {a.glyph}
                </span>
                <div className="starter-card__text" style={{ textAlign: 'left' }}>
                  <div className="starter-card__title">{a.title}</div>
                  <div className="starter-card__desc">{a.desc}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

interface NewEventDesignStepProps {
  onClose: () => void;
  onCreated: (id: string) => void;
  onCreateFailed: (msg: string) => void;
  onSuccess: (name: string) => void;
  onBack: (() => void) | null;
}

function NewEventDesignStep({
  onClose,
  onCreated,
  onCreateFailed,
  onSuccess,
  onBack,
}: NewEventDesignStepProps) {
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const canCreate = name.trim().length >= 3 && !creating;

  const create = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!canCreate) return;
    setCreating(true);
    try {
      const created = await api.createEventDesign({ name: name.trim() });
      onSuccess(created.name);
      onCreated(created.id);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to create event design';
      onCreateFailed(msg);
      setCreating(false);
    }
  };

  return (
    <div
      className="modal-scrim"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="new-event-design-modal"
    >
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 480 }}
      >
        <header className="modal__header">
          <div>
            <div className="modal__eyebrow">New event design</div>
            <h2 className="modal__title">Name your topology</h2>
          </div>
          <button
            type="button"
            className="btn btn--ghost btn--icon-only"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </header>
        <form onSubmit={create} className="modal__body" style={{ padding: '12px 24px 20px' }}>
          <div className="form-row form-row--stacked">
            <label className="form-row__label" htmlFor="new-event-design-name">
              Name <span className="form-row__required">*</span>
            </label>
            <input
              id="new-event-design-name"
              className="input"
              placeholder="e.g. Orders fabric"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              maxLength={120}
              data-testid="new-event-design-name"
              style={{
                height: 32,
                padding: '0 10px',
                background: 'var(--bg-surface-2)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--r-sm)',
                color: 'var(--text-1)',
                fontSize: 13,
              }}
            />
            <div className="form-row__hint">
              You'll wire topics + subscriptions in the canvas after creation.
            </div>
          </div>
          <footer
            className="modal__footer"
            style={{ marginTop: 16, display: 'flex', gap: 8 }}
          >
            {onBack && (
              <button type="button" className="btn btn--ghost" onClick={onBack}>
                Back
              </button>
            )}
            <span style={{ flex: 1 }} />
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={!canCreate}
              data-testid="new-event-design-submit"
            >
              {creating ? 'Creating…' : 'Create & open designer'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
